import { styled } from '@linaria/react';
import { Group, Stack } from '@mantine/core';
import type {
  Component,
  DataEntryComponent,
  PageTemplateComponent,
} from '../../../../api/pages-client/pages-client.type';
import { secondaryBase, skyLight } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { getWidgetIcon } from '../../dashboards.helpers';

type Props = {
  component?: Component | PageTemplateComponent;
  dataEntryComponent?: DataEntryComponent;
};

export const ComponentPreview = ({ component, dataEntryComponent }: Props) => {
  const activeComponent = component || dataEntryComponent;

  if (!activeComponent) {
    return null;
  }

  const isDataEntry = !!dataEntryComponent;
  const componentData = !isDataEntry ? component?.component_data?.[0] : undefined;
  const color = Object.values(componentData?.color_map || {})[0] || secondaryBase;

  const iconName = isDataEntry
    ? getWidgetIcon('data_entry')
    : component
      ? getWidgetIcon(component.type, component.sub_type)
      : 'dashboard';

  const label = (
    <Stack gap={0}>
      <Text size="small" weight="medium">
        {activeComponent.name}
      </Text>
      {activeComponent.description && <Text size="tiny">{activeComponent.description}</Text>}
    </Stack>
  );

  return (
    <Tooltip label={label} variant="black" multiline style={{ maxWidth: 350 }} zIndex={1000}>
      <ComponentGroup gap="xs">
        <Icon name={iconName} size={16} color={color} style={{ flexShrink: 0 }} />
        <Text
          size="small"
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {activeComponent.name}
        </Text>
      </ComponentGroup>
    </Tooltip>
  );
};

const ComponentGroup = styled(Group)`
  overflow: hidden;
  min-width: 0;
  max-width: 100%;
  flex-wrap: nowrap;
  padding: 4px 8px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${skyLight};
  }
`;
